import React, { useState, useEffect } from 'react';
import StatCard from '../../components/StatCard';
import { cloudAPI } from '../../utils/api';

export default function PipelineHealthPage() {
    const [data, setData] = useState(null);
    const [loading, setLoading] = useState(true);

    useEffect(() => {
        cloudAPI.getInfra()
            .then(res => {
                // Backend returns { system_health, ingest_metrics, mv_refreshes }
                // (each is an array of rows). Page renders flat fields plus a
                // services table, so fold the latest rows into that shape.
                const d = res.data || {};
                const ingestRows = Array.isArray(d.ingest_metrics) ? d.ingest_metrics
                                  : Array.isArray(d.rows) ? d.rows
                                  : [];
                const healthRows = Array.isArray(d.system_health) ? d.system_health
                                  : Array.isArray(d.services) ? d.services
                                  : [];
                const latest = ingestRows[0] || {};
                const totalReq = Number(latest.requests) || 0;
                const totalErr = (Number(latest.errors_validation) || 0) + (Number(latest.errors_db_write) || 0);
                const errRate = totalReq > 0 ? Math.round(totalErr / totalReq * 1000) / 10 : 0;
                const services = healthRows.map(r => ({
                    name:        r.name || r.service || r.component || '-',
                    status:      r.status || (r.healthy ? 'healthy' : (r.healthy === false ? 'unhealthy' : 'unknown')),
                    latency_ms:  r.latency_ms ?? r.latency ?? '-',
                    last_check:  r.last_check || r.timestamp || '-',
                }));
                setData({
                    ingest_rate: Number(latest.records_ingested) || 0,
                    queue_depth: Number(d.queue_depth) || 0,
                    error_rate: errRate,
                    uptime: d.uptime || 'N/A',
                    services,
                });
            })
            .catch(err => console.error('Pipeline error:', err))
            .finally(() => setLoading(false));
    }, []);

    if (loading) return <div className="text-gray-400">Loading...</div>;
    if (!data) return <div className="text-red-500">Failed to load</div>;

    return (
        <div>
            <h2 className="text-2xl font-bold text-gray-900 mb-6">Pipeline Health</h2>
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6 mb-8">
                <StatCard title="Ingest Rate" value={data.ingest_rate || 0} subtitle="Events/min" />
                <StatCard title="Queue Depth" value={data.queue_depth || 0} subtitle="Pending events" />
                <StatCard title="Error Rate" value={`${data.error_rate || 0}%`} subtitle="Last 24h" />
                <StatCard title="Uptime" value={data.uptime || 'N/A'} subtitle="System uptime" />
            </div>

            <div className="bg-white rounded-lg shadow-md p-6">
                <h3 className="text-lg font-bold text-gray-900 mb-4">Service Status</h3>
                <div className="overflow-x-auto">
                    <table className="min-w-full divide-y divide-gray-200">
                        <thead className="bg-gray-50">
                            <tr>
                                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Service</th>
                                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Status</th>
                                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Latency</th>
                                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Last Check</th>
                            </tr>
                        </thead>
                        <tbody className="bg-white divide-y divide-gray-200">
                            {(data.services || []).map((svc, idx) => (
                                <tr key={idx} className="hover:bg-gray-50">
                                    <td className="px-6 py-4 whitespace-nowrap text-sm font-medium text-gray-900">{svc.name}</td>
                                    <td className="px-6 py-4 whitespace-nowrap">
                                        <span className={`px-2 py-1 inline-flex text-xs leading-5 font-semibold rounded-full ${
                                            svc.status === 'healthy' ? 'bg-green-100 text-green-800' : 'bg-red-100 text-red-800'
                                        }`}>
                                            {svc.status}
                                        </span>
                                    </td>
                                    <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">{svc.latency_ms || '-'}ms</td>
                                    <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">{svc.last_check || '-'}</td>
                                </tr>
                            ))}
                            {(!data.services || data.services.length === 0) && (
                                <tr><td colSpan="4" className="px-6 py-6 text-sm text-gray-400 text-center">No service data</td></tr>
                            )}
                        </tbody>
                    </table>
                </div>
            </div>
        </div>
    );
}
